import { v4 as uuidv4 } from 'uuid';
import {
    ContentPropertyData,
    ControllableElement,
    CreativeTypes,
    ElementTypes,
    FontFamily,
    getAllElementsRecursively,
    ImageWebSpecificationParser,
    MultiPageImageModel,
    MultiPageVideoModel,
    SpecificationExporter,
    Template,
    TextElement,
    TextStyle,
    VideoWebSpecificationParser,
} from '@bynder-studio/render-core';
import { collectStyleIds } from 'packages/pages/editor/RightSideMenu/Shots/Text/TextStyles/utils';

export type MatchList = { [key: TextStyle['uuid']]: TextStyle['uuid'] };

const collectTextStyles = (selectedElements: ControllableElement[], currentStyles: TextStyle[]) => {
    const textElements = getAllElementsRecursively(selectedElements).filter(
        (element) => element instanceof TextElement,
    );

    return [
        ...new Set(
            (textElements as TextElement[]).flatMap((element) =>
                [...collectStyleIds(element as TextElement)].filter((style) => !!style),
            ),
        ),
    ]
        .map((styleId) => currentStyles.find(({ uuid }) => uuid === styleId))
        .filter((style) => !!style) as TextStyle[];
};

export const convertTextStyles = (
    selectedElements: ControllableElement[],
    currentStyles: TextStyle[],
): [MatchList, TextStyle[]] => {
    const idsMatchList: MatchList = {};

    const textStyles = collectTextStyles(selectedElements, currentStyles).map((style) => {
        const { uuid, ...styleProps } = style;
        const newUuid = uuidv4();
        idsMatchList[uuid] = newUuid;

        return { ...styleProps, uuid: newUuid };
    });

    return [idsMatchList, textStyles as TextStyle[]];
};

export const replaceStyleIds = (
    formattedText: { runs: TextElement['formattedText']['runs'] },
    matchList: MatchList,
) => {
    return formattedText.runs.map((run) => {
        if (run.styleId && matchList[run.styleId]) {
            run.styleId = matchList[run.styleId];
        }

        return run;
    });
};

export const replaceDuplicatedIds = (elements: Template['elements'], matchList: MatchList) => {
    Object.values(elements).forEach((element) => {
        if (element?.children) {
            return replaceDuplicatedIds(element.children, matchList);
        }

        if (element?.type === 'TEXT') {
            const styleId = matchList[element.properties.styleId];

            if (styleId) {
                element.properties.styleId = styleId;
            }

            element.properties.value.runs = replaceStyleIds(element.properties.value, matchList);
        }
    });

    return elements;
};

export type Node<T> = T & { children?: Record<string, Node<T>> };

export const getAllNodesRecursively = <T extends object>(node: Node<T>) => {
    const result: Node<T>[] = [node];

    if (!node?.children) {
        return result;
    }

    Object.values(node.children).forEach((child) => {
        result.push(...getAllNodesRecursively(child));
    });

    return result;
};

export const getContentPropertiesFromElement = (creativeModel: any, elements: any, matchList: MatchList) => {
    if (!creativeModel) {
        return [];
    }

    const allContentProperties = creativeModel.getContentPropertiesManager().toObject();
    const ids = new Set(elements.map((element) => element.contentPropertyUuid as string).filter(Boolean));

    const contentProperties = allContentProperties
        .filter((item) => ids.has(item.uuid))
        .map((item) => {
            const uuid = uuidv4();

            elements.forEach((element) => {
                if (element.contentPropertyUuid !== item.uuid) {
                    return;
                }

                const el = elements.find(({ id }) => element.id === id);

                if (el) {
                    el.contentPropertyUuid = uuid;
                }
            });

            if (item.type === ElementTypes.TEXT) {
                item.properties.textStyles = (item.properties.textStyles ?? []).reduce((acc, styleId) => {
                    const newVal = matchList[styleId];

                    if (newVal) {
                        acc.push(newVal);
                    }

                    return acc;
                }, [] as string[]);

                item.properties.value?.runs?.forEach((run) => {
                    run.styleId = matchList[run.styleId] ?? null;
                });
            }

            return { ...item, uuid };
        });

    return contentProperties;
};

export const assignNewUuid = <T extends { uuid: string }>(items: T[]): Map<string, string> => {
    const idsMap = new Map<string, string>();

    items.map((item) => {
        const id: string = idsMap.get(item.uuid) || uuidv4();
        idsMap.set(item.uuid, id);
        item.uuid = id;
    });

    return idsMap;
};

export const changeElementsContentPropertyIds = <T extends { contentPropertyId?: string }>(
    elements: T[],
    idsMap: Map<string, string>,
) => {
    getAllElementsRecursively(elements).forEach((element) => {
        if (element.contentPropertyId && idsMap.has(element.contentPropertyId)) {
            element.contentPropertyId = idsMap.get(element.contentPropertyId);
        }
    });
};

const DEFAULT_IMAGE_DURATION_SECONDS = 5;
export const getDurationForImageElementsInVideoDesigns = (creativeModel, startFrame) => {
    const playbackDuration = creativeModel.getPlaybackDuration();
    const frameRate = playbackDuration.getFrameRate();
    const totalDuration = playbackDuration.getDuration();
    const defaultDurationInFrames = DEFAULT_IMAGE_DURATION_SECONDS * frameRate;
    const remainingDuration = totalDuration - startFrame;

    return Math.min(defaultDurationInFrames, remainingDuration);
};

export const exportElementsWithStyles = (creativeModel: any, selectedElements: ControllableElement[]) => {
    const [idsMatchList, textStyles] = convertTextStyles(selectedElements, creativeModel.getTextStyles().list);

    const specElements = SpecificationExporter.exportElements(selectedElements) as unknown as Template['elements'];
    Object.values(specElements).forEach((element) => {
        if (element) {
            delete (element as any).parentId;
        }
    });

    let allSpecElements = [];
    Object.values(specElements).forEach((el) => {
        allSpecElements = [...allSpecElements, ...getAllNodesRecursively(el)];
    });

    const contentProperties = getContentPropertiesFromElement(creativeModel, allSpecElements, idsMatchList);
    const syncedElements = replaceDuplicatedIds(specElements, idsMatchList);

    return {
        elements: syncedElements,
        textStyles,
        contentProperties,
    };
};

export type ElementWithRenderOrder = {
    renderOrder?: number;
    children?: ElementWithRenderOrder[];
};

/**
 * Ensure every child's renderOrder sits above its parent group's renderOrder so
 * that when addElements flattens and sorts by renderOrder the group itself comes
 * first and keeps its intended value.  Children are assigned strictly ascending
 * renderOrders that preserve their original relative ordering.
 *
 * Operates in-place. Recurses into nested groups.
 */
export const liftChildrenRenderOrders = (elements: ElementWithRenderOrder[]): void => {
    elements.forEach((element) => {
        if (!element.children || element.children.length === 0) {
            return;
        }

        const groupRO = element.renderOrder ?? 0;
        const sorted = [...element.children].sort((a, b) => (a.renderOrder ?? 0) - (b.renderOrder ?? 0));
        const originalRenderOrders = sorted.map((child) => child.renderOrder ?? 0);

        let nextRO = groupRO + 1;

        sorted.forEach((child, idx) => {
            // Only increment if this child had a different original renderOrder than the previous one.
            // Children that shared the same renderOrder (same layer) must keep sharing one.
            if (idx > 0 && originalRenderOrders[idx] !== originalRenderOrders[idx - 1]) {
                nextRO++;
            }

            child.renderOrder = nextRO;
        });

        // Recursively handle nested groups
        liftChildrenRenderOrders(element.children);
    });
};

export const parseAndApplyElements = (
    creativeModel: MultiPageImageModel | MultiPageVideoModel,
    data: { elements?: any; textStyles?: TextStyle[]; contentProperties?: ContentPropertyData[]; type?: CreativeTypes },
    fonts: FontFamily[],
    options?: {
        startFrame?: number;
        isUniqueContentProperties?: boolean;
    },
) => {
    const isImageDesign = creativeModel instanceof MultiPageImageModel;
    const isVideoDesign = creativeModel instanceof MultiPageVideoModel;

    const webParser = isImageDesign ? new ImageWebSpecificationParser() : new VideoWebSpecificationParser();

    const parsedElements = webParser.parseElements(data as any, fonts);

    const textStyles = creativeModel.getTextStyles();
    const updatedTextStylesIds = textStyles.extendStyles(data.textStyles ?? []);

    let contentProperties: ContentPropertyData[] = data.contentProperties ?? [];

    if (isImageDesign) {
        contentProperties = contentProperties.filter(
            (contentProperty) =>
                contentProperty.type !== ElementTypes.VIDEO && contentProperty.type !== ElementTypes.AUDIO,
        );
    }

    const contentPropertiesManager = creativeModel.getContentPropertiesManager();

    if (options?.isUniqueContentProperties) {
        const idsMap = assignNewUuid(contentProperties);
        contentPropertiesManager.adjustSimilarContentPropertiesNames(contentProperties);
        changeElementsContentPropertyIds(parsedElements, idsMap);
    }

    const updatedContentPropertyIds = contentPropertiesManager.extendContentProperties(
        contentProperties,
        fonts,
        updatedTextStylesIds,
        options?.isUniqueContentProperties,
    );

    let durationForImageElements = 0;

    if (isVideoDesign && data.type === CreativeTypes.IMAGE) {
        durationForImageElements = getDurationForImageElementsInVideoDesigns(creativeModel, options?.startFrame ?? 0);
    }

    const startFrameOffset = options?.startFrame ?? 0;

    // Offset renderOrders so new elements don't conflict with existing ones on canvas.
    // renderOrder is scoped per parent, so only offset top-level elements against existing top-level elements.
    const existingTopLevelElements = creativeModel.getElements();
    const maxExistingRenderOrder = existingTopLevelElements.reduce(
        (max: number, el: any) => Math.max(max, el.renderOrder ?? 0),
        0,
    );

    const minNewRenderOrder = parsedElements.reduce(
        (min: number, el: any) => Math.min(min, el.renderOrder ?? 0),
        Infinity,
    );
    const renderOrderOffset = parsedElements.length > 0 ? maxExistingRenderOrder - minNewRenderOrder + 1 : 0;

    if (renderOrderOffset > 0) {
        parsedElements.forEach((element: any) => {
            element.renderOrder = (element.renderOrder ?? 0) + renderOrderOffset;
        });
    }

    // Ensure children renderOrders sit above their group so addElements/recalculateRenderOrders
    // produce the correct hierarchy.
    liftChildrenRenderOrders(parsedElements);

    const allNewElements = getAllElementsRecursively(parsedElements);

    allNewElements.forEach((element: any) => {
        if (isVideoDesign && data.type === CreativeTypes.IMAGE) {
            element.duration = durationForImageElements;
        }

        if (startFrameOffset > 0) {
            element.startFrame = (element.startFrame ?? 0) + startFrameOffset;

            // Recalculate animation timings to match the new startFrame.
            // animationIn/Out derive their startFrame from el.startFrame on update().
            if (element.animationIn) {
                element.animationIn.update(element.animationIn.config);
            }

            if (element.animationOut) {
                element.animationOut.update(element.animationOut.config);
            }

            if (element.animations?.length) {
                element.animations.forEach((animation) => {
                    const config = animation.config;
                    config.startFrame = (config.startFrame ?? 0) + startFrameOffset;
                    animation.update(config);
                });
            }
        }

        if (updatedContentPropertyIds.has(element.contentPropertyId)) {
            element.contentPropertyId = updatedContentPropertyIds.get(element.contentPropertyId);
        }

        if (element instanceof TextElement) {
            element.textStyles = element.textStyles.filter((styleId) => textStyles.getStyle(styleId));
            element.formattedText.runs.forEach((run) => {
                if (!run.styleId) {
                    return;
                }

                const newId = updatedTextStylesIds.get(run.styleId);

                if (newId) {
                    run.styleId = newId;
                }
            });
        }
    });

    creativeModel.addElements(parsedElements);
};
